import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../api/axios";
import image from "../assets/forgot.png";
import "./ForgotPassword.css";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [strength, setStrength] = useState({ rules: {}, score: 0 });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Invalid or expired reset link.");
        setLinkExpired(true);
        return;
      }

      try {
        await axios.post("/api/auth/v1/verify-reset-token", { token });
      } catch {
        setError("This reset link has expired.");
        setLinkExpired(true);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid or expired reset link.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (strength.score < 5) {
      setError("Password is too weak. Please meet all requirements.");
      return;
    }

    try {
      setLoading(true);

      await axios.post("/api/auth/v1/reset-password", {
        token,
        password,
      });
      navigate("/");
    } catch (err) {
      setError(
        err?.response?.data?.message || "Reset link expired or invalid.",
      );
      setLinkExpired(true);
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = (pwd) => {
    const rules = {
      length: pwd.length >= 8,
      upper: /[A-Z]/.test(pwd),
      lower: /[a-z]/.test(pwd),
      number: /\d/.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    };

    const passed = Object.values(rules).filter(Boolean).length;

    return { rules, score: passed };
  };

  return (
    <div className="login-shell" style={{ fontFamily: "Work Sans" }}>
      <div className="brand-pane">
        <div className="forgot-illustration-wrapper">
          <img
            src={image}
            alt="Reset Password"
            className="forgot-illustration"
          />
        </div>
      </div>

      <div className="form-pane">
        <Paper
          elevation={0}
          className="form-card"
          style={{ backgroundColor: "transparent" }}
        >
          {!linkExpired && (
            <>
              <h2 className="form-title">Reset your password</h2>
              <p className="form-subtitle">
                Please enter a new password for your account.
              </p>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}

              {linkExpired && (
                <div style={{ marginTop: "8px" }}>
                  <Button
                    variant="text"
                    onClick={() => navigate("/")}
                    sx={{ textTransform: "none", padding: 0 }}
                  >
                    Back to Login
                  </Button>
                </div>
              )}
            </Alert>
          )}

          {!linkExpired ? (
            <form onSubmit={handleSubmit} noValidate>
              <div className="field">
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  label="New Password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPassword(value);
                    setStrength(checkPasswordStrength(value));
                  }}
                  margin="normal"
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </div>

              <div className="field">
                <TextField
                  fullWidth
                  type="password"
                  label="Re-type New Password"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                />
              </div>

              {!password && (
                <div
                  style={{
                    fontSize: "13px",
                    color: "#6b7280",
                    marginTop: "6px",
                  }}
                >
                  Use at least 8 characters with uppercase, lowercase, number
                  and special character.
                </div>
              )}

              {password && (
                <div style={{ marginTop: "10px" }}>
                  <div
                    style={{
                      height: "6px",
                      borderRadius: "6px",
                      background: "#e0e0e0",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${(strength.score / 5) * 100}%`,
                        transition: "width 0.3s ease",
                        background:
                          strength.score <= 2
                            ? "#e74c3c" // red
                            : strength.score <= 4
                              ? "#f39c12" // orange
                              : "#2ecc71", // green
                      }}
                    />
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      marginTop: "4px",
                      color: "#555",
                      fontWeight: "500",
                    }}
                  >
                    {strength.score <= 2 && "Weak password"}
                    {strength.score > 2 &&
                      strength.score <= 4 &&
                      "Medium strength"}
                    {strength.score === 5 && "Strong password"}
                  </div>
                </div>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                className="login-cta"
                disabled={
                  !password ||
                  password !== confirmPassword ||
                  strength.score < 5 ||
                  loading
                }
              >
                {loading ? "Saving..." : "Save Password"}
              </Button>
            </form>
          ) : null}
        </Paper>
      </div>
      {/* {linkExpired && (
        <div style={{ marginTop: "8px" }}>
          <Button
            variant="text"
            onClick={() => navigate("/")}
            sx={{ textTransform: "none", padding: 0 }}
          >
            Back to Login
          </Button>
        </div>
      )} */}
    </div>
  );
};

export default ResetPassword;
